import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Button, Dropdown, FormRow, Label, Running, Settings, TextField } from 'basic-components';
import { Query, ReduxService, TimeService } from 'dashboard-services';
import { globalActions, loginActions, userAuthQueries } from 'primary-components';

import { basicSettingsActions } from 'actions/settings';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';
import { routing } from 'routing';
import { AuthSingleton, ExcelUtils, GAUtils, LocalStorageUtils } from 'utils';

import HeaderNoSearch from 'primary/HeaderNoSearch';

import { When } from 'react-if';

import './Settings.scss';

const numberFormatTypes = ExcelUtils.numberFormatTypes
const numberFormatTypesObj = ExcelUtils.numberFormatTypesObj
const timeZones = TimeService.TIME_ZONES.map(t => ReduxService.updateObject(t, { tooltipPosition: "top" }))
const SettingsComponent = ({ isLoggedIn }) => {
  const dispatch = useDispatch(),
        {
          definedTime,
          definedNumberFormat,
        } = useSelector(state => state.settingsState),
        defaultApiUrl = useMemo(() => AuthSingleton.getInstance().getApiUrl(), []),
        defaultTimezoneObject = useMemo(() => timeZones.find(c => c.value === definedTime), [definedTime]),
        defaultNumberFormatObj = useMemo(() => numberFormatTypes.find(c => c.value === definedNumberFormat), [definedNumberFormat]),
        [apiUrl, setApiUrl] = useState(defaultApiUrl),
        [timezone, setTimezone] = useState(definedTime),
        [numberFormat, setNumberFormat] = useState(definedNumberFormat),
        getQueryBuilderNoError = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withShouldDisplayError(false)
        , [dispatch]),
        { mutateAsync: logOutCall } = userAuthQueries.useLogOutMutation({ getQueryBuilder: getQueryBuilderNoError }),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig())), [dispatch]),
        { mutateAsync: updateSettingsCall } = userAuthQueries.useUpdateCurrentUserSettings({ getQueryBuilder }),
        saveApiUrl = useCallback(({ numberFormatLocal = numberFormat, timezoneLocal = timezone, apiUrlLocal = apiUrl }) => {
          let promise = Promise.resolve()
          if((definedTime !== timezoneLocal || definedNumberFormat !== numberFormatLocal) && isLoggedIn && (timeZoneRef.current.validateForForm() && numberFormatRef.current.validateForForm())) {
            promise = updateSettingsCall({
                settings: {
                  definedTime: timeZones.find(s => s?.value === timezoneLocal)?.value || TimeService.UTC,
                  definedNumberFormat: numberFormatTypes.find(s => s?.value === numberFormatLocal)?.value || numberFormatTypesObj.GENERAL
                }
              })
              .then(() => {
                batch(() => {
                  dispatch(globalActions.getMessageStore()).success("Settings have been saved.")
                  dispatch(basicSettingsActions.onChange(timeZones.find(s => s?.value === timezoneLocal)?.value || TimeService.UTC, "definedTime"))
                  dispatch(basicSettingsActions.onChange(numberFormatTypes.find(s => s?.value === numberFormatLocal).value || numberFormatTypesObj.GENERAL, "definedNumberFormat"))
                })
              })
          }
          if(apiUrlLocal !== defaultApiUrl && apiUrlRef.current.validateForForm()) {
            promise.then(() => {
              if(isLoggedIn) {
                dispatch(loginActions.logOut({ logOutCall, withSideEffects: false }))
              }
              LocalStorageUtils.setApiUrl(apiUrlLocal)
              dispatch(globalActions.getMessageStore()).success("API URL has been changed.")
            })
          }
        }, [apiUrl, defaultApiUrl, definedNumberFormat, definedTime, dispatch, isLoggedIn, logOutCall, numberFormat, timezone, updateSettingsCall]),
        resetValues = useCallback(() => {
          setApiUrl(properties.baseUrl.api)
          setTimezone(TimeService.UTC)
          setNumberFormat(numberFormatTypesObj.GENERAL)
          timeZoneRef.current?.dropdown?.current?.setValue({ label: TimeService.UTC, value: TimeService.UTC })
          numberFormatRef.current?.dropdown?.current?.setValue({ label: numberFormatTypes.find(s => s?.value === numberFormatTypesObj.GENERAL)?.label, value: numberFormatTypesObj.GENERAL })
          dispatch(globalActions.getToastsStore()).warning("Values have been reset to default. Do you want to save them now?", "Values have been reset to default.", {
            withCancel: false,
            confirmText: "Save",
            confirmAction: () => saveApiUrl({ numberFormatLocal: numberFormatTypesObj.GENERAL, timezoneLocal: TimeService.UTC, apiUrlLocal: properties.baseUrl.api })
          })
        }, [dispatch, saveApiUrl]),
        apiUrlRef = useRef(),
        timeZoneRef = useRef(),
        numberFormatRef = useRef()

  useEffect(() => {
    GAUtils.sendEvent({
      category: "open",
      action: "open_settings",
      label: "open"
    })
    GAUtils.sendPageView(routing.SETTINGS.name)
  }, [])

  return (
    <div 
        className="ng-office-app__settings"
    >
      <HeaderNoSearch
          icon={(
            <Settings
                color="grey-dark"
                height={20}
                width={20}
            />
          )}
          title="ADD-IN SETTINGS"
      />
      <div className="ng-office-app__settings__body">
        <div className="ng-office-app__settings__body__section">
          <div className="ng-office-app__settings__body__section__title">
            ADD-IN VERSION
          </div>
          <FormRow 
              className="ng-office-app__settings__body__section__body"
              isVertical
          >
            <Label   
                size="big"
            >
              Version: {properties.version}
            </Label>
          </FormRow>
        </div>
        <div className="ng-office-app__settings__body__section">
          <div className="ng-office-app__settings__body__section__title">
            API CONFIGURATION
          </div>
          <FormRow 
              className="ng-office-app__settings__body__section__body"
              isVertical
          >
            <Label>API URL</Label>
            <TextField
                defaultValue={apiUrl}
                name="apiUrl"
                onChange={setApiUrl}
                ref={apiUrlRef}
                required
                variant="border"
            />
          </FormRow>
        </div>
        <When condition={isLoggedIn}>
          {() => (
            <div className="ng-office-app__settings__body__section">
              <div className="ng-office-app__settings__body__section__title">
                USER SETTINGS
              </div>
              <FormRow
                  className="ng-office-app__settings__body__section__body"
                  isVertical
              >
                <Label>User time zone</Label>
                <Dropdown
                    defaultValue={defaultTimezoneObject}
                    name="definedTime"
                    onChange={setTimezone}
                    options={timeZones}
                    ref={timeZoneRef}
                    required
                    variant="border"
                />
              </FormRow>
              <FormRow
                  className="ng-office-app__settings__body__section__body"
                  isVertical
              >
                <Label>Excel Data Type</Label>
                <Dropdown
                    defaultValue={defaultNumberFormatObj}
                    name="numberFormat"
                    onChange={setNumberFormat}
                    options={numberFormatTypes}
                    ref={numberFormatRef}
                    required
                    variant="border"
                />
              </FormRow>
            </div>
          )}
        </When>
      </div>
      <div className="ng-office-app__settings__footer">
        <Button
            onClick={resetValues}
            size="small"
            variant="text"
        >
          <Running
              bgColor="white-base"
              color="blue-bright"
              height={16}
              width={16}
          />
          <strong>
            Reset
          </strong>
        </Button>
        <Button
            disabled={apiUrl === defaultApiUrl && ((definedTime === timezone && definedNumberFormat === numberFormat) || !isLoggedIn)}
            onClick={saveApiUrl}
        >
          Save
        </Button>
        <div/>
      </div>
    </div>
  )
}

SettingsComponent.defaultProps = {
  isLoggedIn: false
}

SettingsComponent.propTypes = {
  isLoggedIn: PropTypes.bool
}

export default SettingsComponent;